"use client";

import { useState, useEffect } from "react";
import { useSession } from "next-auth/react";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Star,
  Mail,
  Building2,
  Briefcase,
  MessageSquare,
  ExternalLink,
  Shield,
  User as UserIcon,
  Loader2,
} from "lucide-react";
import {
  addFavoriteContact,
  removeFavoriteContact,
  createOrGetConversation,
} from "@/app/actions/messaging";
import { getUserProfile } from "@/app/actions/user";
import { toast } from "sonner";

interface UserInfo {
  id: string;
  name: string | null;
  email: string | null;
  image: string | null;
}

interface UserProfile {
  id: string;
  name: string | null;
  email: string | null;
  image: string | null;
  role: string;
  firstName: string | null;
  lastName: string | null;
  profile?: {
    company: string | null;
    role: string | null;
    bio: string | null;
    licenseNumber: string | null;
    licenseState: string | null;
    yearsExperience: number | null;
    brokerage?: {
      name: string;
    } | null;
  } | null;
}

interface UserProfilePopoverProps {
  user: UserInfo;
  isFavorite?: boolean;
  onFavoriteChange?: () => void;
  onStartConversation?: (conversationId: string) => void;
  children: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  showMessageButton?: boolean;
}

export function UserProfilePopover({
  user,
  isFavorite = false,
  onFavoriteChange,
  onStartConversation,
  children,
  side = "right",
  align = "start",
  showMessageButton = true,
}: UserProfilePopoverProps) {
  const { data: session } = useSession();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
  const [isStartingConversation, setIsStartingConversation] = useState(false);
  const [localIsFavorite, setLocalIsFavorite] = useState(isFavorite);

  // Update local favorite state when prop changes
  useEffect(() => {
    setLocalIsFavorite(isFavorite);
  }, [isFavorite]);

  // Fetch user profile when popover opens
  useEffect(() => {
    if (open && !profile && user.id) {
      fetchUserProfile();
    }
  }, [open, user.id]);

  async function fetchUserProfile() {
    setLoading(true);
    try {
      const result = await getUserProfile(user.id);
      if (result.error) {
        console.error("Failed to fetch user profile:", result.error);
      } else if (result.user) {
        setProfile(result.user);
      }
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleToggleFavorite() {
    if (!session?.user?.id || session.user.id === user.id) return;

    setIsTogglingFavorite(true);
    try {
      const result = localIsFavorite
        ? await removeFavoriteContact(user.id)
        : await addFavoriteContact(user.id);

      if (result.error) {
        toast.error(result.error);
      } else {
        setLocalIsFavorite(!localIsFavorite);
        toast.success(
          localIsFavorite ? "Removed from contacts" : "Added to contacts"
        );
        onFavoriteChange?.();
      }
    } catch (error) {
      console.error("Failed to toggle favorite:", error);
      toast.error("Failed to update contact");
    } finally {
      setIsTogglingFavorite(false);
    }
  }

  async function handleStartConversation() {
    if (!session?.user?.id) return;

    setIsStartingConversation(true);
    try {
      const result = await createOrGetConversation(user.id);

      if (result.error) {
        toast.error(result.error);
      } else if (result.conversation) {
        toast.success("Opening conversation");
        onStartConversation?.(result.conversation.id);
        setOpen(false);
      }
    } catch (error) {
      console.error("Failed to start conversation:", error);
      toast.error("Failed to start conversation");
    } finally {
      setIsStartingConversation(false);
    }
  }

  const getDisplayName = () => {
    if (profile?.name) return profile.name;
    if (profile?.firstName && profile?.lastName) {
      return `${profile.firstName} ${profile.lastName}`;
    }
    if (profile?.firstName) return profile.firstName;
    if (user.name) return user.name;
    return user.email?.split("@")[0] || "Unknown User";
  };

  const getInitials = () => {
    const name = getDisplayName();
    const parts = name.split(" ");
    if (parts.length >= 2 && parts[0] && parts[1]) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Don't show popover for own user
  if (session?.user?.id === user.id) {
    return <>{children}</>;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-500/50 rounded-full"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        className={cn(
          "w-80 p-0",
          "bg-gray-900/95 backdrop-blur-xl",
          "border border-cyan-500/30",
          "shadow-[0_8px_32px_rgba(0,0,0,0.5),0_0_0_1px_rgba(6,182,212,0.2)]"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? (
          <div className="p-4 space-y-4">
            <div className="flex items-center gap-3">
              <Skeleton className="h-16 w-16 rounded-full bg-gray-800/50" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-32 bg-gray-800/50" />
                <Skeleton className="h-4 w-24 bg-gray-800/50" />
              </div>
            </div>
            <Skeleton className="h-10 w-full bg-gray-800/50" />
          </div>
        ) : (
          <>
            {/* Header with avatar */}
            <div className="p-4 border-b border-cyan-500/20 bg-gradient-to-r from-gray-800/50 to-gray-900/50">
              <div className="flex items-start gap-4">
                <div className="relative">
                  <Avatar className="h-16 w-16 ring-2 ring-cyan-500/30">
                    <AvatarImage
                      src={profile?.image || user.image || undefined}
                      alt={getDisplayName()}
                    />
                    <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-blue-500 text-white text-lg font-semibold">
                      {getInitials()}
                    </AvatarFallback>
                  </Avatar>
                  {localIsFavorite && (
                    <div className="absolute -top-1 -right-1 h-6 w-6 bg-yellow-500 rounded-full border-2 border-gray-900 flex items-center justify-center shadow-lg shadow-yellow-500/50">
                      <Star className="h-3.5 w-3.5 text-gray-900 fill-gray-900" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-white truncate">
                      {getDisplayName()}
                    </h3>
                    {profile?.role === "ADMIN" && (
                      <Badge
                        variant="outline"
                        className="text-xs border-cyan-500/50 text-cyan-400 flex items-center gap-1"
                      >
                        <Shield className="h-3 w-3" />
                        Admin
                      </Badge>
                    )}
                  </div>

                  {/* Email */}
                  {(profile?.email || user.email) && (
                    <a
                      href={`mailto:${profile?.email || user.email}`}
                      className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-cyan-400 transition-colors"
                    >
                      <Mail className="h-3.5 w-3.5" />
                      <span className="truncate">
                        {profile?.email || user.email}
                      </span>
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Profile Details */}
            <div className="p-4 space-y-3">
              {/* Company/Brokerage */}
              {(profile?.profile?.company || profile?.profile?.brokerage) && (
                <div className="flex items-start gap-2">
                  <Building2 className="h-4 w-4 text-gray-500 mt-0.5 shrink-0" />
                  <div className="text-sm">
                    {profile?.profile?.role && (
                      <span className="text-gray-300">
                        {profile.profile.role}
                      </span>
                    )}
                    {profile?.profile?.role && (profile?.profile?.company || profile?.profile?.brokerage) && (
                      <span className="text-gray-500"> at </span>
                    )}
                    <span className="text-cyan-400">
                      {profile?.profile?.brokerage?.name || profile?.profile?.company}
                    </span>
                  </div>
                </div>
              )}

              {/* License Info */}
              {profile?.profile?.licenseNumber && (
                <div className="flex items-start gap-2">
                  <Briefcase className="h-4 w-4 text-gray-500 mt-0.5 shrink-0" />
                  <div className="text-sm text-gray-400">
                    License: {profile.profile.licenseNumber}
                    {profile.profile.licenseState && (
                      <span> ({profile.profile.licenseState})</span>
                    )}
                  </div>
                </div>
              )}

              {/* Years Experience */}
              {profile?.profile?.yearsExperience && (
                <div className="flex items-start gap-2">
                  <UserIcon className="h-4 w-4 text-gray-500 mt-0.5 shrink-0" />
                  <span className="text-sm text-gray-400">
                    {profile.profile.yearsExperience} years experience
                  </span>
                </div>
              )}

              {/* Bio */}
              {profile?.profile?.bio && (
                <p className="text-sm text-gray-400 line-clamp-3 border-t border-cyan-500/10 pt-3 mt-3">
                  {profile.profile.bio}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="p-4 pt-0 flex gap-2">
              {/* Add/Remove Contact Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleToggleFavorite}
                disabled={isTogglingFavorite}
                className={cn(
                  "flex-1",
                  localIsFavorite
                    ? "border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                    : "border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                )}
              >
                {isTogglingFavorite ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Star
                    className={cn(
                      "h-4 w-4 mr-2",
                      localIsFavorite && "fill-yellow-500"
                    )}
                  />
                )}
                {localIsFavorite ? "Remove Contact" : "Add Contact"}
              </Button>

              {/* Message Button */}
              {showMessageButton && (
                <Button
                  size="sm"
                  onClick={handleStartConversation}
                  disabled={isStartingConversation}
                  className={cn(
                    "flex-1",
                    "bg-cyan-500/20 hover:bg-cyan-500/30",
                    "text-cyan-400 hover:text-cyan-300",
                    "border border-cyan-500/30 hover:border-cyan-500/50"
                  )}
                >
                  {isStartingConversation ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <MessageSquare className="h-4 w-4 mr-2" />
                  )}
                  Message
                </Button>
              )}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
